import { ReactNode, useEffect, useState } from "react"
import CloseButton from "../../components/CloseButton/CloseButton"
import Price from "../../appData/price"
import { useAppState } from "../../context/appStateContext"
import { useBlackFriday } from "../../context/blackFridayContext"
import useStrings from "../../localization/useStrings"
import native from "../../native/native"
import usePromoCountdown from "../../context/usePromoCountdown"
import styles from "./SeasonalBanner.module.scss"

export interface SeasonalBannerColors {
  containerBackground: string
  titleText: string
  subtitleText: string
  urgencyText: string
  countdownBox: string
  countdownValue: string
  countdownLabel: string
  ctaBackground: string
  ctaText: string
}

interface SeasonalBannerProps {
  icon: ReactNode
  title: string
  colors: SeasonalBannerColors
  titleFontSize?: string
}

const Countdown = ({ colors }: { colors: SeasonalBannerColors }) => {
  const strings = useStrings()
  const { minutes, hours, seconds } = usePromoCountdown()

  return (
    <div className={styles.countdown}>
      <div
        className={styles.countdownItem}
        style={{ background: colors.countdownBox }}
      >
        <span
          className={styles.countdownValue}
          style={{ color: colors.countdownValue }}
        >
          {hours.toString().padStart(2, "0")}
        </span>
        <span
          className={styles.countdownLabel}
          style={{ color: colors.countdownLabel }}
        >
          {strings.summerSale.hours}
        </span>
      </div>
      <div
        className={styles.countdownItem}
        style={{ background: colors.countdownBox }}
      >
        <span
          className={styles.countdownValue}
          style={{ color: colors.countdownValue }}
        >
          {minutes.toString().padStart(2, "0")}
        </span>
        <span
          className={styles.countdownLabel}
          style={{ color: colors.countdownLabel }}
        >
          {strings.summerSale.minutes}
        </span>
      </div>
      <div
        className={styles.countdownItem}
        style={{ background: colors.countdownBox }}
      >
        <span
          className={styles.countdownValue}
          style={{ color: colors.countdownValue }}
        >
          {seconds.toString().padStart(2, "0")}
        </span>
        <span
          className={styles.countdownLabel}
          style={{ color: colors.countdownLabel }}
        >
          {strings.summerSale.seconds}
        </span>
      </div>
    </div>
  )
}

const SeasonalBanner = ({
  icon,
  title,
  colors,
  titleFontSize = "18px",
}: SeasonalBannerProps) => {
  const strings = useStrings()
  const blackFriday = useBlackFriday()
  const initialData = useAppState().state.initialData

  const isAlbumList = initialData?.source == "albumListTop"
  const closeVisible = isAlbumList

  const [visible, setVisible] = useState(false)

  // Calculate pricing using same approach as PromoBanner
  const isUltimate =
    blackFriday.state.isUltimate ||
    blackFriday.state.isPremiumUser ||
    blackFriday.state.isUltimateLaunch

  const pricing = isUltimate
    ? blackFriday.state.ultimatePricing
    : blackFriday.state.premiumPricing
  const savingPercentage = pricing.savingPercent

  const monthlyEquivalent = new Price(
    pricing.yearlyPriceDiscounted.value / 12,
    pricing.baseProduct.priceLocale,
  ).formatted

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div
      className={`${styles.container} ${
        visible ? styles.fadeInShow : styles.fadeIn
      }`}
      style={{ background: colors.containerBackground }}
    >
      {closeVisible && (
        <CloseButton
          className={styles.close}
          onClick={() => {
            native.postNativeEvent("close")
          }}
        />
      )}

      <div className={styles.content}>
        <div className={styles.headerContainer}>
          <div className={styles.icon}>{icon}</div>
          <div className={styles.textContent}>
            <h2
              className={styles.title}
              style={{ color: colors.titleText, fontSize: titleFontSize }}
            >
              {title} — {strings.summerSale.savePercent(savingPercentage)}
            </h2>
            <p
              className={styles.subtitle}
              style={{ color: colors.subtitleText }}
            >
              {isUltimate
                ? strings.summerSale.bannerSubtitleUltimate(
                    monthlyEquivalent + "/month",
                  )
                : strings.summerSale.bannerSubtitle(
                    monthlyEquivalent + "/month",
                  )}
            </p>
          </div>
        </div>

        <Countdown colors={colors} />

        <p className={styles.urgency} style={{ color: colors.urgencyText }}>
          {strings.summerSale.timeRemaining}
        </p>
        <button
          className={styles.cta}
          style={{ background: colors.ctaBackground, color: colors.ctaText }}
          onClick={() => {
            blackFriday.actions.openPaywallFromBanner()
          }}
        >
          {isUltimate
            ? strings.summerSale.getUltimate
            : strings.summerSale.getPremium}
        </button>
      </div>
    </div>
  )
}

export default SeasonalBanner
